import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Lock, UploadCloud, Share2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, galleryHeaders, mediaUrl, saveGrant } from "@/lib/api";
import { useI18n, EVENT_TYPES, labelOf } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";
import { PrivacyBadge } from "@/components/EventCard";
import { useAuth } from "@/context/AuthContext";
import ContributorUpload from "@/components/ContributorUpload";
import SupportBlock from "@/components/SupportBlock";
import SmartImage from "@/components/SmartImage";

export default function EventDetail() {
  const { slug } = useParams();
  const { t, path, localized, formatDate, lang } = useI18n();
  const { user, isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${slug}`, { headers: galleryHeaders(slug) });
      setEvent(data);
    } catch (e) {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post(`/events/${slug}/unlock`, { password });
      saveGrant(slug, data.access_token);
      setPassword("");
      load();
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    }
  };

  if (loading) return <p className="p-12 text-sm text-[#5A5752]">{t("common.loading")}</p>;
  if (!event) return <p className="p-12 text-sm text-[#8F3329]">404</p>;

  const name = localized(event, "name", event.fallback_language);
  const desc = localized(event, "description", event.fallback_language);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12 sm:px-8 lg:px-12 lg:py-16">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs text-[#5A5752]" data-testid="event-breadcrumb">
        <Link to={path("events")} className="hover:text-[#8F3329]">
          {t("events.title")}
        </Link>
        <span className="mx-2 text-[#C69A3B]">/</span>
        <span className="text-[#252525]">{name}</span>
      </nav>

      <header className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-start">
        <div>
          <p className="label-mono">{labelOf(EVENT_TYPES, event.event_type, lang)}</p>
          <h1 className="mt-4 font-serif-display text-4xl leading-tight tracking-tight text-[#8F3329] sm:text-5xl">
            {name}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-[#5A5752]">
            <span className="inline-flex items-center gap-2">
              <CalendarDays size={15} className="text-[#C69A3B]" /> {formatDate(event.event_date)}
            </span>
            {event.location && (
              <span className="inline-flex items-center gap-2">
                <MapPin size={15} className="text-[#C69A3B]" /> {event.location}
              </span>
            )}
            <PrivacyBadge privacy={event.privacy} />
          </div>
          {desc && <p className="mt-6 max-w-2xl text-sm text-[#5A5752] sm:text-base">{desc}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            {event.sharing_enabled && event.privacy === "public" && (
              <GoldButton
                variant="outline"
                data-testid="event-share-btn"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.href);
                  toast.success(t("gallery.linkCopied"));
                }}
              >
                <Share2 size={15} /> {t("btn.share")}
              </GoldButton>
            )}
            {event.authorized && user && (isAdmin || event.contributions_enabled) && (
              <GoldButton data-testid="event-contribute-btn" onClick={() => setShowUpload((v) => !v)}>
                <UploadCloud size={15} /> {isAdmin ? t("upload.adminTitle") : t("btn.uploadPhotos")}
              </GoldButton>
            )}
          </div>
        </div>
        {event.cover_image && (
          <div className="photo-hover overflow-hidden border border-[#C69A3B]/40">
            <SmartImage src={event.cover_image} alt={name} className="aspect-[4/3] w-full" />
          </div>
        )}
      </header>

      {showUpload && event.authorized && (
        <div className="mt-10">
          <ContributorUpload slug={slug} event={event} albums={event.albums || []} onDone={() => { setShowUpload(false); load(); }} />
        </div>
      )}

      {!event.authorized ? (
        <section className="mt-14 max-w-lg border border-[#C69A3B] bg-[#FAFAF7] p-8" data-testid="gallery-lock-panel">
          <Lock className="text-[#8F3329]" size={22} />
          <h2 className="mt-4 font-serif-display text-xl text-[#252525]">
            {event.privacy === "private" ? t("privacy.noticePrivate") : t("privacy.noticePassword")}
          </h2>
          {event.privacy === "password" && (
            <form onSubmit={unlock} className="mt-6 space-y-3">
              <label className="label-mono block" htmlFor="event-password">
                {t("privacy.password.label")}
              </label>
              <input
                id="event-password"
                type="password"
                data-testid="event-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="min-h-[48px] w-full border border-[#C69A3B]/60 bg-white px-4 text-base"
                required
              />
              {error && (
                <p data-testid="event-password-error" className="text-sm text-[#8F3329]">
                  {error}
                </p>
              )}
              <GoldButton type="submit" data-testid="event-unlock-btn">
                {t("btn.unlock")}
              </GoldButton>
            </form>
          )}
          {event.privacy === "private" && !user && (
            <Link
              to={path("login")}
              data-testid="private-login-link"
              className="mt-6 inline-flex min-h-[44px] items-center border border-[#C69A3B] px-5 text-sm text-[#8F3329] hover:bg-[#8F3329] hover:text-[#FAFAF7]"
            >
              {t("btn.login")}
            </Link>
          )}
          <p className="mt-7 border-t border-[#C69A3B]/40 pt-5 text-sm text-[#5A5752]">
            {t("privacy.noPassword")}{" "}
            <a href="mailto:hrcreations90@gmail.com" data-testid="request-access-email" className="text-[#8F3329] underline">
              hrcreations90@gmail.com
            </a>
          </p>
        </section>
      ) : (
        <section className="mt-16">
          <div
            className="mb-10 flex flex-wrap items-center gap-3 border-l-2 border-[#C69A3B] bg-[#FAFAF7] px-5 py-4"
            data-testid="gallery-guide-notice"
          >
            <BookOpen size={16} className="text-[#C69A3B]" />
            <p className="text-sm text-[#5A5752]">{t("guide.galleryNotice")}</p>
            <Link
              to={path("guide")}
              data-testid="gallery-guide-link"
              className="text-sm text-[#8F3329] underline transition-colors duration-300 hover:text-[#252525]"
            >
              {t("guide.galleryLink")}
            </Link>
          </div>
          <p className="label-mono">{t("events.albums")}</p>
          <div className="gold-rule mt-5 w-24" />
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="albums-grid">
            {(event.albums || []).map((al) => (
              <Link
                key={al.id}
                to={path("events", slug, al.slug)}
                data-testid={`album-card-${al.slug}`}
                className="photo-hover group overflow-hidden border border-[#C69A3B]/30 bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#D8D3CB]">
                  {(al.cover_media_id || al.cover_image) && (
                    <SmartImage
                      src={
                        al.cover_media_id
                          ? mediaUrl({ id: al.cover_media_id }, "thumb", slug)
                          : al.cover_image
                      }
                      focusKey={`album-${al.id}`}
                      alt={localized(al, "name")}
                      loading="lazy"
                      className="h-full w-full"
                    />
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-serif-display text-lg text-[#252525]">{localized(al, "name")}</h3>
                  <p className="label-mono mt-2">
                    {al.photo_count > 0 && `${al.photo_count} ${t("events.photos")}`}
                    {al.photo_count > 0 && al.video_count > 0 && " · "}
                    {al.video_count > 0 && `${al.video_count} ${t("events.videos")}`}
                    {al.photo_count === 0 && al.video_count === 0 && `0 ${t("events.items")}`}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {event.support_enabled && event.support_qr && (
            <SupportBlock qrImage={event.support_qr} note={localized(event, "support_note")} />
          )}
        </section>
      )}
    </div>
  );
}
